import * as React from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom-v5-compat';

import ByStudent from './by_student'
import BySkill from './by_skill'

import { ALL_PRACTICE, NO_CLASSROOM, renderNoClassroomEmptyState, } from '../../shared/shared'
import { ClassroomInterface, SkillCategoryInterface, StudentPerformanceData, SkillPerformanceData } from '../interfaces';
import { useReportPremiumContext } from '../../shared/report_premium_context';
import {
  Spinner,
} from '../../../../../Shared/index';

interface PerformanceBreakdownProps {
  classroom: ClassroomInterface;
  filteredSkillCategories: SkillCategoryInterface[];
  filteredSkillSubCategories: SkillCategoryInterface[];
  handleAssignTargetedPracticeClick: (skillId: number, studentIds: number[]) => void;
  includeELL: boolean;
  momentumScoreGoal: number;
  onSkillCategoryClick: (skillName: string) => void;
  onStudentChange: (student: any) => void;
  practiceScope: string;
  renderHeader: (title: string) => React.ReactNode;
  renderNeedsVerificationEmptyState: () => React.ReactNode;
  renderUpgradeEmptyState: () => React.ReactNode;
  renderVerificationBanner: () => React.ReactNode;
  selectedSkillCategoryId: any;
  selectedSkillSubCategoryId: any;
  selectedStudentId: any;
  skillData: {[key: string]: SkillPerformanceData[]};
  skillDataKey: string;
  studentData: {[key: string]: StudentPerformanceData[]};
  studentDataKey: string;
  userNeedsVerification: boolean;
}

const PerformanceBreakdown: React.FC<PerformanceBreakdownProps> = ({
  classroom,
  filteredSkillCategories,
  filteredSkillSubCategories,
  handleAssignTargetedPracticeClick,
  includeELL,
  momentumScoreGoal,
  onSkillCategoryClick,
  onStudentChange,
  practiceScope,
  renderHeader,
  renderNeedsVerificationEmptyState,
  renderUpgradeEmptyState,
  renderVerificationBanner,
  selectedSkillCategoryId,
  selectedSkillSubCategoryId,
  selectedStudentId,
  skillData,
  skillDataKey,
  studentData,
  studentDataKey,
  userNeedsVerification,
}) => {
  const { hasSchoolOrDistrictPremium } = useReportPremiumContext();
  const navigate = useNavigate();
  const location = useLocation();

  const containerRef = React.useRef<HTMLDivElement>(null)
 
  const byStudent = location.pathname.includes('by_student');

  function handleStudentClick(studentId: number | string) {
    onStudentChange({ value: Number(studentId) });
    navigate('by_skill');
  }

  const selectedSkillCategory = filteredSkillCategories.find(skillCategory => skillCategory.id === selectedSkillCategoryId)
  const selectedSkillSubCategory = filteredSkillSubCategories.find(skillSubCategory => skillSubCategory.id === selectedSkillSubCategoryId)

  const sharedProps = {
    classroom,
    containerRef,
    includeELL,
    momentumScoreGoal,
    practiceScope,
    selectedSkillCategory
  }

  const routes = (
    <Routes>
      <Route
        element={
          <ByStudent
            {...sharedProps}
            data={studentData[studentDataKey]}
            handleAssignTargetedPracticeClick={handleAssignTargetedPracticeClick}
            onStudentClick={handleStudentClick}
            selectedSkillSubCategory={selectedSkillSubCategory}
          />
        }
        path="by_student"
      />
      <Route
        element={
          <BySkill
            {...sharedProps}
            data={skillData[skillDataKey]}
            onSkillCategoryClick={onSkillCategoryClick}
            studentId={selectedStudentId}
          />
        }
        path="by_skill"
      />
    </Routes>
  )

  if (!classroom || (byStudent && !selectedSkillCategory)) { return <Spinner /> }

  function renderContent() {
    if (!hasSchoolOrDistrictPremium && practiceScope === ALL_PRACTICE) {
      return renderUpgradeEmptyState()
    }

    if (userNeedsVerification && practiceScope === ALL_PRACTICE) {
      return renderNeedsVerificationEmptyState()
    }

    if (classroom.id === NO_CLASSROOM.id) {
      return renderNoClassroomEmptyState()
    }

    return routes
  }


  return (
    <main className="performance-breakdown" data-testid="performance-breakdown" ref={containerRef}>
      {renderHeader('Performance Breakdown')}
      {renderVerificationBanner()}
      <div className="performance-breakdown-content">
        {renderContent()}
      </div>
    </main>
  );
};

export default PerformanceBreakdown;
